import React, { useState } from "react";
import { FaQuoteLeft, FaCopy, FaCheck } from "react-icons/fa";
import { citeAPA, citeMLA, citeChicago } from "../utils/citations";

const FORMATS = [
  { id: "apa", label: "APA (7th)", build: citeAPA },
  { id: "mla", label: "MLA (9th)", build: citeMLA },
  { id: "chicago", label: "Chicago (author-date)", build: citeChicago }
];

export default function CiteThis({ research, theme = "light" }) {
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState("apa");
  const [copiedId, setCopiedId] = useState(null);

  if (!research?._id) return null;

  const isDark = theme === "dark";

  const copy = async (id, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      window.prompt("Copy citation:", text);
    }
  };

  const active = FORMATS.find((f) => f.id === activeId) || FORMATS[0];
  const citationText = active.build(research);

  const panelClass = isDark
    ? "border border-gray-600 bg-gray-800/80"
    : "border border-gray-200 bg-gray-50";
  const tabActive = isDark
    ? "bg-blue-900 text-blue-100 border-blue-600"
    : "bg-white text-blue-800 border-blue-300 shadow-sm";
  const tabIdle = isDark
    ? "bg-gray-800 text-gray-300 border-gray-600 hover:bg-gray-700"
    : "bg-white/60 text-gray-600 border-gray-200 hover:bg-white";

  return (
    <div className={isDark ? "text-gray-200" : "text-gray-900"}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 w-full sm:w-auto text-left font-semibold rounded-lg px-3 py-2 transition-colors ${
          isDark
            ? "bg-gray-800 text-blue-300 hover:bg-gray-700 border border-gray-600"
            : "bg-indigo-50 text-indigo-900 hover:bg-indigo-100 border border-indigo-200"
        }`}
        aria-expanded={open}
      >
        <FaQuoteLeft className="shrink-0 opacity-80" />
        Cite this
        <span className="text-xs font-normal opacity-70 ml-1">{open ? "−" : "+"}</span>
      </button>

      {open && (
        <div className={`mt-3 rounded-lg p-4 ${panelClass}`}>
          <p className={`text-xs mb-3 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
            Simplified citations for this repository entry. Adapt punctuation and italics to your style guide if needed.
          </p>
          <div className="flex flex-wrap gap-2 mb-3">
            {FORMATS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setActiveId(f.id)}
                className={`px-3 py-1.5 rounded-md text-sm border ${
                  activeId === f.id ? tabActive : tabIdle
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="relative">
            <pre
              className={`whitespace-pre-wrap break-words text-sm leading-relaxed p-3 rounded-md pr-12 ${
                isDark ? "bg-gray-900 text-gray-100" : "bg-white text-gray-800 border border-gray-100"
              }`}
            >
              {citationText}
            </pre>
            <button
              type="button"
              onClick={() => copy(active.id, citationText)}
              className={`absolute top-2 right-2 p-2 rounded-md transition-colors ${
                isDark
                  ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              title="Copy to clipboard"
              aria-label="Copy citation"
            >
              {copiedId === active.id ? (
                <FaCheck className="text-green-500" />
              ) : (
                <FaCopy />
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
